import type {
  FormatPerformanceDto,
  MediaFormat,
  MetricsCoverageDto,
} from '@devmil/contracts';
import {useId} from 'react';

import {
  describeSourcedMetric,
  formatDays,
  formatMetricValue,
  formatSample,
  formatShortDate,
} from '../../lib/format.js';

/** Nome de tela de cada formato. Fechado: formato novo aparece no build. */
const NOME_DO_FORMATO: Record<MediaFormat, string> = {
  reel: 'Reel',
  story: 'Story',
  photo: 'Foto',
  carousel: 'Carrossel',
};

/**
 * A comparação que o editorial precisa para aprender.
 *
 * O achado F13 dizia que o agente não consegue aprender com comparação válida,
 * e o motivo era duplo: a consulta de mídia usava nomes de Reels para todos os
 * formatos, então Story e foto não tinham número nenhum; e não havia lugar na
 * tela onde formato e idade fossem comparados lado a lado.
 *
 * Três colunas seguram a honestidade desta tabela:
 *
 * - **idade**, porque o acumulado de uma peça sobe com o tempo, e comparar um
 *   Reel de um dia com uma foto de sete é comparar idades, não formatos;
 * - **amostra**, com denominador, porque "os Reels alcançaram 800" quer dizer
 *   coisas opostas se for uma peça medida de oito ou oito de oito;
 * - **versão**, porque a mesma métrica muda de definição entre versões da Graph,
 *   e a média de números medidos em versões diferentes é a média de coisas
 *   diferentes.
 *
 * O carrossel aparece antes de existir. A linha vazia com motivo diz que o
 * formato não tem medição, e ela se preenche sozinha no dia em que a esteira
 * publicar o primeiro, sem ninguém mexer nesta tela.
 */
export const FormatTable = ({
  performance,
  coverage,
}: {
  performance: readonly FormatPerformanceDto[];
  coverage: MetricsCoverageDto;
}) => {
  const headingId = useId();

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Alcance por formato e idade</h2>
        <span className="dm-illustrative">
          {coverage.lastMeasuredAt === null
            ? 'SEM COLETA'
            : `COLETA DE ${formatShortDate(coverage.lastMeasuredAt)}`}
        </span>
      </div>
      <div className="dm-table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Formato</th>
              <th scope="col">Idade</th>
              <th scope="col">Alcance médio</th>
              <th scope="col">Amostra</th>
              <th scope="col">Origem</th>
            </tr>
          </thead>
          <tbody>
            {performance.map((linha) => (
              <tr
                key={`${linha.format}-${linha.ageDays}`}
                data-testid="format-row"
              >
                <td>{NOME_DO_FORMATO[linha.format]}</td>
                <td>{formatDays(linha.ageDays)}</td>
                <td>{formatMetricValue(linha.average)}</td>
                <td>{formatSample(linha.average.sample)}</td>
                <td>
                  <code>
                    {describeSourcedMetric(
                      linha.average,
                      linha.average.availability === 'available'
                        ? linha.average.sourceRevision
                        : '',
                    )}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dm-piece-meta" data-testid="metrics-coverage">
        {`${coverage.measuredPieces} de ${coverage.publishedPieces} peças publicadas na janela foram medidas`}
        {coverage.piecesWithoutFormat > 0
          ? `; ${coverage.piecesWithoutFormat} sem formato legível, que não têm como ser medidas`
          : ''}
      </p>
    </section>
  );
};
